import React from "react";
import {
  Heading,
  Button,
  Badge,
  Avatar,
  AvatarFallback,
  Icon,
} from "@elaniintc/goes-design-system";
import type { TagType } from "@/types/entities/tags";
import { MAX_FILE_SIZE_BYTES } from "./NewTagModal.helpers";

interface NewTagImageUploadProps {
  imagePreview: string | null;
  isDragging: boolean;
  setIsDragging: (value: boolean) => void;
  handleDrop: (e: React.DragEvent<HTMLDivElement>) => void;
  handleImageChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  handleRemoveImage: () => void;
  image: File | null;
  isEditing: boolean;
  editData?: TagType;
  invalidImage: { name: string; size: number } | null;
  imageError?: string;
}

export const NewTagImageUpload = ({
  imagePreview,
  isDragging,
  setIsDragging,
  handleDrop,
  handleImageChange,
  handleRemoveImage,
  image,
  isEditing,
  editData,
  invalidImage,
  imageError,
}: NewTagImageUploadProps) => {
  return (
    <div>
      <Heading variant="h6" className="mb-2 text-body-loud">
        Imagen
      </Heading>

      {/* Solo mostrar zona de carga si NO hay imagen existente */}
      {!imagePreview && (
        <section
          aria-label="Área de subida de archivos"
          className={`border-2 border-dashed rounded-xl p-4 flex flex-col items-center justify-center gap-2 transition-colors duration-200 ${
            isDragging
              ? "border-neutral bg-surface-action-accent-quiet"
              : "border-neutral"
          }`}
          style={{ minHeight: 120 }}
          onDragOver={(e) => {
            e.preventDefault();
            e.stopPropagation();
            setIsDragging(true);
          }}
          onDragLeave={(e) => {
            e.preventDefault();
            e.stopPropagation();
            setIsDragging(false);
          }}
          onDrop={handleDrop}
        >
          {isDragging ? (
            <div className="flex flex-col items-center justify-center gap-2">
              <Avatar shape="square">
                <AvatarFallback color="action" shape="square">
                  <Icon
                    type="far"
                    name="arrow-up"
                    className="text-base text-action-quiet mb-1 w-3"
                  />
                </AvatarFallback>
              </Avatar>
              <span className="text-action-quiet font-medium">
                Suelta tu imagen aquí
              </span>
            </div>
          ) : (
            <>
              <span className="text-body text-sm">
                Arrastra y Suelta o{" "}
                <label className="text-action cursor-pointer font-medium">
                  Selecciona una imagen{" "}
                  <input
                    type="file"
                    accept="image/jpeg,image/jpg"
                    onChange={handleImageChange}
                    className="hidden"
                  />
                </label>
              </span>
              <span className="text-xs text-body-quiet">
                Sube una imagen .jpg (máximo 1MB)
              </span>
            </>
          )}
          {imageError && (
            <span className="text-danger text-xs">{imageError || ""}</span>
          )}
        </section>
      )}

      {/* Preview de la imagen */}
      {imagePreview && (
        <div className="flex flex-col gap-3">
          {/* Imagen válida */}
          <div className="w-full text-body flex items-center justify-between border rounded-lg p-3 border-neutral">
            <div className="flex items-center gap-3 flex-1 min-w-0">
              <div
                className={`rounded-lg overflow-hidden bg-neutral-object ${
                  !image && isEditing ? "w-7 h-7" : "w-12 h-12"
                }`}
              >
                <img
                  src={imagePreview}
                  alt="Preview"
                  className="w-full h-full object-cover"
                />
              </div>
              <span className="truncate overflow-hidden text-sm min-w-0 flex-1">
                {image?.name || "Imagen actual"}
              </span>
            </div>
            <div className="flex items-center gap-3 shrink-0">
              {/* Badge para archivo nuevo subido */}
              {image && (
                <Badge variant="neutral" size="xs" shape="pill">
                  {(image.size / 1024 / 1024).toFixed(2)} MB
                </Badge>
              )}
              {/* Badge para imagen existente en modo edición */}
              {!image && isEditing && editData?.files?.[0]?.size && (
                <Badge variant="neutral" size="xs" shape="pill">
                  {(editData.files[0].size / 1024 / 1024).toFixed(2)} MB
                </Badge>
              )}
              <Button
                variant="ghost"
                size="xs"
                type="button"
                className="cursor-pointer"
                onClick={handleRemoveImage}
              >
                <Icon type="far" name="trash" className="text-current" />
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Mensaje de error para imagen inválida */}
      {invalidImage && !imagePreview && (
        <div className="flex flex-col gap-1">
          <span className="text-xs text-danger flex items-center gap-1 px-3 py-2 bg-danger-surface rounded-lg">
            <Icon
              type="far"
              name="circle-xmark"
              className="text-danger text-sm"
            />
            {invalidImage.size > MAX_FILE_SIZE_BYTES
              ? "Imagen muy pesada, máximo 1MB permitido."
              : "Solo se permiten archivos JPG."}
          </span>
        </div>
      )}
    </div>
  );
};
